import { useState, useEffect, useRef } from "react";
import { Users, Scale, Megaphone, TrendingUp, Calendar, GraduationCap, Monitor, Landmark, Heart, Vote, BookOpen, Zap, FileText, Building2, CreditCard, ClipboardList, ExternalLink, Mail, MapPin } from "lucide-react";

const COLORS = {
  black: "#0a0a0a",
  gold: "#C9A84C",
  goldLight: "#E8C96A",
  goldDark: "#A07830",
  red: "#8B1A1A",
  cream: "#F5F0E8",
  charcoal: "#1E1E1E",
  gray: "#6B6B6B",
  lightGray: "#F0EDE8",
  seiu: "#582C83",
};

const style = (obj) => obj;

// ── EMAIL CONFIG ─────────────────────────────────────────────────────────────
const FORMSPREE_MEMBERSHIP = "https://formspree.io/f/xvzywagd";
const FORMSPREE_CONTACT    = "https://formspree.io/f/xwvzropy";
// ─────────────────────────────────────────────────────────────────────────────

// ── NAV ──────────────────────────────────────────────────────────────────────
function Nav({ page, setPage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const links = ["Home", "Our Story", "Timeline", "Membership", "Get Involved", "States", "Contact", "Donation", "Board", "Merch", "Organize", "Conference"];

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      background: COLORS.black, borderBottom: `2px solid ${COLORS.gold}`,
      padding: "0 2rem", display: "flex", alignItems: "center",
      justifyContent: "space-between", height: "64px",
    }}>
      {/* Logo */}
      <div
        onClick={() => setPage("Home")}
        style={{ cursor: "pointer", display: "flex", flexDirection: "column", lineHeight: 1 }}
      >
        <span style={{ color: COLORS.gold, fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "1.2rem", letterSpacing: "0.12em" }}>AFRAM</span>
        <span style={{ color: COLORS.cream, fontFamily: "Georgia, serif", fontSize: "0.55rem", letterSpacing: "0.3em" }}>WESTERN REGION</span>
      </div>

      {/* Desktop links */}
      <div style={{ display: "flex", gap: "0.2rem", alignItems: "center" }} className="desktop-nav">
        {links.map(l => (
          <button key={l} onClick={() => setPage(l)} style={{
            background: (page === l || (l === "States" && page.startsWith("State_"))) ? COLORS.gold : "transparent",
            color: (page === l || (l === "States" && page.startsWith("State_"))) ? COLORS.black : COLORS.cream,
            border: "none", padding: "0.4rem 0.9rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.72rem", letterSpacing: "0.08em",
            borderRadius: "2px", transition: "all 0.2s",
          }}>{l.toUpperCase()}</button>
        ))}
        <button onClick={() => setPage("Membership")} style={{
          background: COLORS.gold, color: COLORS.black, border: "none",
          padding: "0.5rem 1.2rem", cursor: "pointer", fontFamily: "Georgia, serif",
          fontSize: "0.72rem", letterSpacing: "0.1em", fontWeight: 700,
          borderRadius: "2px", marginLeft: "0.5rem",
        }}>JOIN NOW</button>
        <button onClick={() => setPage("Donation")} style={{
          background: COLORS.red, color: COLORS.cream, border: "none",
          padding: "0.5rem 1.2rem", cursor: "pointer", fontFamily: "Georgia, serif",
          fontSize: "0.72rem", letterSpacing: "0.1em", fontWeight: 700,
          borderRadius: "2px", marginLeft: "0.3rem",
        }}>DONATE</button>
      </div>

      {/* Mobile hamburger */}
      <button onClick={() => setMenuOpen(!menuOpen)} style={{
        display: "none", background: "transparent", border: "none",
        color: COLORS.gold, fontSize: "1.5rem", cursor: "pointer",
      }} className="hamburger">☰</button>

      {menuOpen && (
        <div style={{
          position: "fixed", top: "64px", left: 0, right: 0,
          background: COLORS.charcoal, padding: "1rem",
          display: "flex", flexDirection: "column", gap: "0.5rem",
        }}>
          {links.map(l => (
            <button key={l} onClick={() => { setPage(l); setMenuOpen(false); }} style={{
              background: "transparent", color: COLORS.cream, border: "none",
              padding: "0.7rem", cursor: "pointer", fontFamily: "Georgia, serif",
              fontSize: "0.9rem", textAlign: "left",
            }}>{l}</button>
          ))}
        </div>
      )}
    </nav>
  );
}

// ── HERO ─────────────────────────────────────────────────────────────────────
function HeroSection({ setPage }) {
  return (
    <section style={{
      minHeight: "100vh",
      background: `linear-gradient(160deg, ${COLORS.black} 0%, #1a0f02 50%, #0d0800 100%)`,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "6rem 2rem 4rem", position: "relative", overflow: "hidden",
    }}>
      {/* Decorative gold lines */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, bottom: 0, opacity: 0.07,
        backgroundImage: `repeating-linear-gradient(90deg, ${COLORS.gold} 0px, ${COLORS.gold} 1px, transparent 1px, transparent 80px)`,
        pointerEvents: "none",
      }} />

      <div style={{ textAlign: "center", maxWidth: "780px", position: "relative" }}>
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", marginBottom: "1.5rem", flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ border: `1px solid ${COLORS.gold}`, padding: "0.3rem 1.2rem", fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.35em" }}>SEIU &#183; AFRICAN AMERICAN CAUCUS</div>
          <div style={{ background: COLORS.seiu, padding: "0.3rem 0.9rem", borderRadius: "2px", fontFamily: "Georgia, serif", color: "#fff", fontSize: "0.65rem", letterSpacing: "0.2em", fontWeight: 700 }}>SEIU MEMBER CAUCUS</div>
        </div>

        <h1 style={{
          fontFamily: "'Playfair Display', serif", color: COLORS.cream,
          fontSize: "clamp(2.8rem, 7vw, 5.5rem)", lineHeight: 1.05,
          fontWeight: 900, marginBottom: "0.5rem",
        }}>
          AFRAM<br />
          <span style={{ color: COLORS.gold }}>WEST</span>
        </h1>

        <p style={{
          fontFamily: "Georgia, serif", color: "#bbb", fontSize: "1.1rem",
          lineHeight: 1.7, marginBottom: "2.5rem", maxWidth: "560px", margin: "0 auto 2.5rem",
        }}>
          Empowering African American workers across the Western Region through solidarity, advocacy, and collective action.
        </p>

        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => setPage("Membership")} style={{
            background: COLORS.gold, color: COLORS.black, border: "none",
            padding: "0.9rem 2.5rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.9rem", fontWeight: 700,
            letterSpacing: "0.15em", borderRadius: "2px",
          }}>JOIN OUR COMMUNITY</button>
          <button onClick={() => setPage("Our Story")} style={{
            background: "transparent", color: COLORS.cream,
            border: `1px solid ${COLORS.gold}`, padding: "0.9rem 2.5rem",
            cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "0.9rem",
            letterSpacing: "0.1em", borderRadius: "2px",
          }}>OUR STORY</button>
        </div>
      </div>

      {/* Bottom stats bar */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: COLORS.gold, display: "flex", justifyContent: "center",
        gap: "4rem", padding: "1rem 2rem", flexWrap: "wrap",
      }}>
        {[["AZ · CA · CO · NV · WA · OR", "Western States"], ["Est. 1985", "Founded"], ["12,000+", "Members"]].map(([num, label]) => (
          <div key={label} style={{ textAlign: "center" }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "1.1rem", color: COLORS.black }}>{num}</div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: "0.65rem", letterSpacing: "0.2em", color: "#333" }}>{label.toUpperCase()}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── MISSION ───────────────────────────────────────────────────────────────────
function MissionSection({ setPage }) {
  const pillars = [
    { Icon: Users,      title: "Solidarity", desc: "Standing united with African American workers across every sector of the SEIU." },
    { Icon: Scale,      title: "Equity",     desc: "Dismantling systemic racism and institutional disparities in the workplace and beyond." },
    { Icon: Megaphone,  title: "Advocacy",   desc: "Amplifying Black voices in labor negotiations, policy, and community decisions." },
    { Icon: TrendingUp, title: "Growth",     desc: "Developing the next generation of African American labor leaders in the West." },
  ];

  return (
    <section style={{ background: COLORS.cream, padding: "5rem 2rem" }}>
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "3rem" }}>
          <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.7rem" }}>WHAT WE STAND FOR</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.black, fontSize: "2.5rem", fontWeight: 900 }}>Our Mission</h2>
          <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1rem auto 0" }} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "1.5rem" }}>
          {pillars.map(p => (
            <div key={p.title} style={{
              background: COLORS.black, padding: "2rem 1.5rem", borderRadius: "2px",
              borderTop: `3px solid ${COLORS.gold}`,
            }}>
              <p.Icon size={32} color={COLORS.gold} style={{ marginBottom: "0.8rem" }} />
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.2rem", marginBottom: "0.5rem" }}>{p.title}</h3>
              <p style={{ fontFamily: "Georgia, serif", color: "#bbb", fontSize: "0.85rem", lineHeight: 1.65 }}>{p.desc}</p>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: "3rem" }}>
          <button onClick={() => setPage("Membership")} style={{
            background: COLORS.red, color: COLORS.cream, border: "none",
            padding: "0.85rem 2.5rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.85rem", letterSpacing: "0.12em", fontWeight: 700, borderRadius: "2px",
          }}>BECOME A MEMBER TODAY</button>
        </div>
      </div>
    </section>
  );
}

// ── HOME ──────────────────────────────────────────────────────────────────────
function HomePage({ setPage }) {
  return (
    <>
      <HeroSection setPage={setPage} />
      <MissionSection setPage={setPage} />

      {/* Quick CTA banner */}
      <section style={{
        background: COLORS.black, borderTop: `1px solid ${COLORS.goldDark}`,
        borderBottom: `1px solid ${COLORS.goldDark}`,
        padding: "3rem 2rem", textAlign: "center",
      }}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "2rem", marginBottom: "1rem" }}>
          Ready to Make a Difference?
        </h2>
        <p style={{ fontFamily: "Georgia, serif", color: "#aaa", marginBottom: "2rem", maxWidth: "500px", margin: "0 auto 2rem" }}>
          Join thousands of African American workers across the Western Region who are fighting for justice, equity, and dignity in the workplace.
        </p>
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => setPage("Membership")} style={{
            background: COLORS.gold, color: COLORS.black, border: "none",
            padding: "0.85rem 2rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.85rem", fontWeight: 700, letterSpacing: "0.12em", borderRadius: "2px",
          }}>JOIN NOW</button>
          <button onClick={() => setPage("Membership")} style={{
            background: "transparent", color: COLORS.gold, border: `1px solid ${COLORS.gold}`,
            padding: "0.85rem 2rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.85rem", letterSpacing: "0.12em", borderRadius: "2px",
          }}>RENEW MEMBERSHIP</button>
        </div>
      </section>

      {/* Donate CTA */}
      <section style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #6b1010 100%)", padding: "4rem 2rem", textAlign: "center" }}>
        <div style={{ maxWidth: "700px", margin: "0 auto" }}>
          <div style={{ color: "rgba(255,255,255,0.6)", fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>SUPPORT THE MOVEMENT</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: "#fff", fontSize: "clamp(1.8rem,4vw,2.8rem)", fontWeight: 900, marginBottom: "1rem" }}>Every Dollar Fights for Justice</h2>
          <p style={{ fontFamily: "Georgia, serif", color: "rgba(255,255,255,0.8)", fontSize: "1rem", lineHeight: 1.7, marginBottom: "2rem" }}>Your donation funds organizing, leadership development, scholarships, and advocacy for African American workers across the Western Region.</p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => setPage("Donation")} style={{ background: COLORS.gold, color: COLORS.black, border: "none", padding: "1rem 2.5rem", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "1rem", fontWeight: 700, letterSpacing: "0.15em", borderRadius: "2px" }}>DONATE NOW</button>
            <button onClick={() => setPage("Membership")} style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.5)", padding: "1rem 2.5rem", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "1rem", letterSpacing: "0.1em", borderRadius: "2px" }}>BECOME A MEMBER</button>
          </div>
        </div>
      </section>

      {/* Leadership preview */}
      <section style={{ background: COLORS.lightGray, padding: "5rem 2rem" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center" }}>
          <div style={{ color: COLORS.goldDark, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.7rem" }}>WESTERN REGION</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.black, fontSize: "2.2rem", fontWeight: 900, marginBottom: "0.5rem" }}>Our Leadership</h2>
          <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "0 auto 2.5rem" }} />

          <div style={{ display: "flex", justifyContent: "center", gap: "2rem", flexWrap: "wrap" }}>
            {[
              { name: "Ibrahim Coulibaly", role: "President – Western Region" },
              { name: "Western Region VP", role: "Vice President" },
              { name: "Western Region Sec.", role: "Secretary/Treasurer" },
            ].map(leader => (
              <div key={leader.name} style={{
                background: COLORS.black, color: COLORS.cream,
                padding: "2rem 1.5rem", borderRadius: "2px", width: "220px",
                borderBottom: `3px solid ${COLORS.gold}`,
              }}>
                <div style={{
                  width: "70px", height: "70px", borderRadius: "50%",
                  background: COLORS.gold, margin: "0 auto 1rem",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.8rem",
                }}>✦</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1rem", marginBottom: "0.3rem", color: COLORS.gold }}>{leader.name}</div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: "0.75rem", color: "#aaa", letterSpacing: "0.05em" }}>{leader.role}</div>
              </div>
            ))}
          </div>
          <button onClick={() => setPage("Our Story")} style={{
            marginTop: "2rem", background: "transparent", color: COLORS.goldDark,
            border: `1px solid ${COLORS.goldDark}`, padding: "0.7rem 2rem",
            cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "0.8rem",
            letterSpacing: "0.1em", borderRadius: "2px",
          }}>LEARN MORE ABOUT US</button>
        </div>
      </section>
    </>
  );
}

// ── OUR STORY ─────────────────────────────────────────────────────────────────
function OurStoryPage({ setPage }) {
  return (
    <div style={{ paddingTop: "64px", background: COLORS.cream, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: COLORS.black, padding: "5rem 2rem 4rem",
        textAlign: "center", borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>SEIU AFRAM WESTERN REGION</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Our Story</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0" }} />
      </div>

      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "4rem 2rem" }}>
        {[
          {
            heading: "Who We Are",
            body: "AFRAM West is the Western Region chapter of the SEIU African American Caucus — a powerful coalition of Black workers united within the Service Employees International Union. We represent African American SEIU members in California, Arizona, Nevada, Washington, Oregon, and across the Western states.",
          },
          {
            heading: "Our Purpose",
            body: "AFRAM West exists to amplify the voices of African American workers in SEIU, ensure equitable representation in union leadership, and combat systemic racism within both the workplace and the broader labor movement. We believe that racial justice and labor justice are inseparable.",
          },
          {
            heading: "Our People",
            body: "From healthcare workers and janitors to security officers and childcare providers, our members span every sector of the service economy. We are united by a common experience — and a common determination to build a more just and equitable world for ourselves, our families, and our communities.",
          },
          {
            heading: "Our Connection to SEIU AFRAM",
            body: "The Western Region operates as part of SEIU AFRAM's national structure, working alongside the Central, Eastern, and Southern Regions under the leadership of the National Executive Board. Our regional chapter focuses on the unique challenges and opportunities facing Black workers in the West.",
          },
        ].map((section, i) => (
          <div key={i} style={{ marginBottom: "3rem" }}>
            <h2 style={{
              fontFamily: "'Playfair Display', serif", color: COLORS.black, fontSize: "1.7rem",
              fontWeight: 900, marginBottom: "1rem", paddingLeft: "1rem",
              borderLeft: `4px solid ${COLORS.gold}`,
            }}>{section.heading}</h2>
            <p style={{ fontFamily: "Georgia, serif", color: "#333", fontSize: "1rem", lineHeight: 1.8 }}>{section.body}</p>
          </div>
        ))}

        <div style={{
          background: COLORS.black, padding: "2.5rem", borderRadius: "2px",
          borderLeft: `4px solid ${COLORS.gold}`, marginTop: "1rem",
        }}>
          <p style={{
            fontFamily: "'Playfair Display', serif", color: COLORS.cream,
            fontSize: "1.3rem", lineHeight: 1.7, fontStyle: "italic",
          }}>
            "At AFRAM, we are united in purpose and driven by a vision of justice and equity for all. While the challenges evolve, our resolve to confront and dismantle systemic racism grows stronger."
          </p>
        </div>

        <div style={{ textAlign: "center", marginTop: "3rem" }}>
          <button onClick={() => setPage("Timeline")} style={{
            background: COLORS.gold, color: COLORS.black, border: "none",
            padding: "0.85rem 2.5rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.85rem", fontWeight: 700, letterSpacing: "0.12em", borderRadius: "2px",
          }}>VIEW OUR HISTORY →</button>
        </div>
      </div>
    </div>
  );
}

// ── TIMELINE ──────────────────────────────────────────────────────────────────
function TimelinePage() {
  const events = [
    { year: "1968", title: "Roots of the Movement", desc: "African American SEIU members begin organizing informally to address racial inequities within the union and their workplaces. The civil rights era galvanizes Black workers across the Western states." },
    { year: "1972", title: "First Western Caucus Meetings", desc: "African American workers in California and neighboring states hold the first organized regional meetings, laying the groundwork for what will become AFRAM West." },
    { year: "1985", title: "AFRAM Founded", desc: "The SEIU African American Caucus (AFRAM) is officially founded nationally, with the Western Region among the first chapters established. Marchel Smiley emerges as a foundational leader of the movement." },
    { year: "1990", title: "Western Region Charter", desc: "AFRAM West receives its formal charter, establishing governance structures, bylaws, and a regional leadership council representing California, Nevada, Arizona, Oregon, and Washington." },
    { year: "1996", title: "Justice for Janitors Campaign", desc: "AFRAM West members play a pivotal role in the historic Justice for Janitors campaign in Los Angeles, winning landmark contracts for thousands of predominantly Black and brown service workers." },
    { year: "2001", title: "Healthcare Worker Expansion", desc: "Rapid growth in healthcare sector membership doubles AFRAM West's ranks. The region begins hosting annual leadership institutes to develop emerging Black union leaders." },
    { year: "2008", title: "Political Action Initiative", desc: "AFRAM West launches a major political action program, mobilizing African American SEIU members across the West in historic numbers during the presidential election cycle." },
    { year: "2012", title: "Fight for $15 Leadership", desc: "Western Region members spearhead SEIU's Fight for $15 campaign, with African American fast food and home care workers leading rallies from San Francisco to Las Vegas." },
    { year: "2015", title: "30th Anniversary Milestone", desc: "AFRAM celebrates its 30th anniversary. The Western Region reaches 10,000 active members and launches a new scholarship fund for members' children pursuing higher education." },
    { year: "2020", title: "Black Lives Matter & Labor", desc: "In the wake of the murder of George Floyd, AFRAM West leads SEIU in issuing a landmark racial justice action plan, connecting labor rights to the broader struggle for Black liberation." },
    { year: "2022", title: "Ibrahim Coulibaly Elected", desc: "Ibrahim Coulibaly is elected President of the Western Region, becoming one of the youngest regional presidents in AFRAM history. He prioritizes member engagement and digital organizing." },
    { year: "2024", title: "AFRAMWest Digital Launch", desc: "AFRAM West launches an expanded digital presence and online membership portal, making it easier than ever for African American SEIU members across the region to join, renew, and get involved." },
    { year: "2025", title: "Building the Future", desc: "AFRAM West continues its mission of solidarity, equity, and advocacy — growing stronger with each new member who joins the cause. The next chapter is being written by you." },
  ];

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, #0a0a0a 0%, #1a0f02 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>OUR LEGACY</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>History & Timeline</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "500px", margin: "0 auto" }}>
          Decades of struggle, solidarity, and progress — the story of African American workers in the Western Region.
        </p>
      </div>

      {/* Timeline */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}>
        <div style={{ position: "relative" }}>
          {/* Center line */}
          <div style={{
            position: "absolute", left: "50%", top: 0, bottom: 0,
            width: "2px", background: `linear-gradient(to bottom, ${COLORS.gold}, ${COLORS.goldDark})`,
            transform: "translateX(-50%)",
          }} />

          {events.map((event, i) => (
            <div key={i} style={{
              display: "flex",
              flexDirection: i % 2 === 0 ? "row" : "row-reverse",
              marginBottom: "2.5rem",
              alignItems: "flex-start",
              gap: "0",
            }}>
              {/* Content */}
              <div style={{
                width: "calc(50% - 30px)",
                background: COLORS.charcoal,
                padding: "1.5rem",
                borderRadius: "2px",
                borderTop: `2px solid ${COLORS.gold}`,
                marginRight: i % 2 === 0 ? "30px" : 0,
                marginLeft: i % 2 === 1 ? "30px" : 0,
              }}>
                <div style={{
                  fontFamily: "'Playfair Display', serif", color: COLORS.gold,
                  fontSize: "1.6rem", fontWeight: 900, marginBottom: "0.3rem",
                }}>{event.year}</div>
                <h3 style={{
                  fontFamily: "Georgia, serif", color: COLORS.cream,
                  fontSize: "0.95rem", fontWeight: 700, marginBottom: "0.6rem",
                  letterSpacing: "0.05em",
                }}>{event.title}</h3>
                <p style={{
                  fontFamily: "Georgia, serif", color: "#999",
                  fontSize: "0.82rem", lineHeight: 1.7,
                }}>{event.desc}</p>
              </div>

              {/* Center dot */}
              <div style={{
                width: "16px", height: "16px", borderRadius: "50%",
                background: COLORS.gold, border: `3px solid ${COLORS.black}`,
                flexShrink: 0, marginTop: "1.5rem",
                position: "relative", zIndex: 1,
              }} />

              {/* Spacer */}
              <div style={{ width: "calc(50% - 30px)" }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── MEMBERSHIP ────────────────────────────────────────────────────────────────
function MembershipPage() {
  const [formType, setFormType] = useState("join"); // "join" | "renew"
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    employer: "", local: "", memberID: "", state: "",
    joinDate: "", membershipType: "standard",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      const res = await fetch(FORMSPREE_MEMBERSHIP, {
        method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          _subject: `AFRAM West ${formType === "join" ? "New Member Application" : "Membership Renewal"} — ${formData.firstName} ${formData.lastName}`,
          formType: formType === "join" ? "New Member Sign-Up" : "Membership Renewal",
          firstName: formData.firstName, lastName: formData.lastName,
          email: formData.email, phone: formData.phone || "(not provided)",
          employer: formData.employer, seiuLocal: formData.local || "(not selected)",
          state: formData.state || "(not selected)", membershipTier: formData.membershipType,
          ...(formType === "renew" && { memberID: formData.memberID || "(not provided)", originalJoinDate: formData.joinDate || "(not provided)" }),
        }),
      });
      if (res.ok) { setSubmitted(true); }
      else { alert("Problem submitting. Please email membership@aframwest.org directly."); }
    } catch { alert("Network error. Please email membership@aframwest.org directly."); }
  };

  const inputStyle = {
    width: "100%", background: "#111", border: `1px solid ${COLORS.goldDark}`,
    color: COLORS.cream, padding: "0.75rem 1rem", fontFamily: "Georgia, serif",
    fontSize: "0.9rem", borderRadius: "2px", boxSizing: "border-box",
    outline: "none",
  };

  const labelStyle = {
    fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.72rem",
    letterSpacing: "0.15em", display: "block", marginBottom: "0.4rem",
  };

  const tiers = [
    {
      id: "standard", name: "Standard Member", price: "$15/yr",
      donation: null, status: null,
      perks: ["Full voting rights", "Monthly newsletter", "Regional event access", "Member resources & directory"],
    },
    {
      id: "sustaining", name: "Sustaining Member", price: "$25/yr",
      donation: "$15 donation included", status: "Sustaining Member status",
      perks: ["All Standard benefits", "Leadership directory", "Priority event registration", "Digital resource library", "$15 goes directly to AFRAM programs"],
    },
    {
      id: "founding", name: "Founding Patron", price: "$50/yr",
      donation: "$25 donation included", status: "Legacy Member status",
      perks: ["All Sustaining benefits", "Recognition in publications", "Steering committee access", "Legacy Member status honor", "$25 goes directly to AFRAM programs"],
    },
  ];

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.black} 0%, #1a0f00 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>JOIN THE MOVEMENT</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Membership</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "500px", margin: "0 auto" }}>
          Become part of a powerful community of African American workers fighting for justice, equity, and dignity.
        </p>
      </div>

      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}>

        {/* Tier Selection */}
        <div style={{ marginBottom: "3rem" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.8rem", marginBottom: "0.5rem", textAlign: "center" }}>Choose Your Membership</h2>
          <div style={{ width: "50px", height: "2px", background: COLORS.gold, margin: "0 auto 2rem" }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "1.2rem" }}>
            {tiers.map(t => (
              <div
                key={t.id}
                onClick={() => setFormData({ ...formData, membershipType: t.id })}
                style={{
                  background: formData.membershipType === t.id ? `linear-gradient(135deg, ${COLORS.goldDark}, ${COLORS.gold})` : COLORS.charcoal,
                  border: `2px solid ${formData.membershipType === t.id ? COLORS.gold : "#333"}`,
                  padding: "1.8rem 1.5rem", borderRadius: "2px", cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                <div style={{
                  fontFamily: "'Playfair Display', serif",
                  color: formData.membershipType === t.id ? COLORS.black : COLORS.gold,
                  fontSize: "1.2rem", fontWeight: 900, marginBottom: "0.3rem",
                }}>{t.name}</div>
                <div style={{
                  fontFamily: "Georgia, serif",
                  color: formData.membershipType === t.id ? COLORS.black : COLORS.cream,
                  fontSize: "1.5rem", fontWeight: 700, marginBottom: "0.4rem",
                }}>{t.price}</div>
                {t.donation && (
                  <div style={{
                    display: "inline-block", marginBottom: "0.4rem",
                    background: formData.membershipType === t.id ? "rgba(0,0,0,0.15)" : "rgba(201,168,76,0.12)",
                    border: `1px solid ${formData.membershipType === t.id ? "rgba(0,0,0,0.2)" : COLORS.goldDark}`,
                    borderRadius: "2px", padding: "0.15rem 0.5rem",
                    fontFamily: "Georgia, serif", fontSize: "0.68rem", letterSpacing: "0.08em",
                    color: formData.membershipType === t.id ? "#333" : COLORS.gold,
                  }}>{t.donation}</div>
                )}
                {t.status && (
                  <div style={{
                    display: "block", marginBottom: "0.9rem",
                    fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.1em", fontStyle: "italic",
                    color: formData.membershipType === t.id ? "#444" : "#777",
                  }}>✦ {t.status}</div>
                )}
                {!t.donation && <div style={{ marginBottom: "1rem" }} />}
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {t.perks.map(p => (
                    <li key={p} style={{
                      fontFamily: "Georgia, serif",
                      color: formData.membershipType === t.id ? "#222" : "#aaa",
                      fontSize: "0.8rem", lineHeight: 1.8,
                      paddingLeft: "1.2rem", position: "relative",
                    }}>
                      <span style={{ position: "absolute", left: 0, color: formData.membershipType === t.id ? COLORS.black : COLORS.gold }}>✦</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p style={{
            fontFamily: "Georgia, serif", color: "#555", fontSize: "0.72rem",
            textAlign: "center", marginTop: "1rem", letterSpacing: "0.03em",
          }}>
            * All memberships cover a full calendar year (January 1 – December 31) regardless of join date.
          </p>
        </div>

        {/* Form type toggle */}
        <div style={{ display: "flex", marginBottom: "2rem", borderBottom: `1px solid #333` }}>
          {["join", "renew"].map(type => (
            <button key={type} onClick={() => setFormType(type)} style={{
              flex: 1, padding: "1rem", background: "transparent",
              border: "none", borderBottom: formType === type ? `3px solid ${COLORS.gold}` : "3px solid transparent",
              color: formType === type ? COLORS.gold : "#666",
              fontFamily: "Georgia, serif", fontSize: "0.9rem", letterSpacing: "0.1em",
              cursor: "pointer", transition: "all 0.2s",
            }}>
              {type === "join" ? "NEW MEMBER SIGN-UP" : "RENEW MY MEMBERSHIP"}
            </button>
          ))}
        </div>

        {/* Form */}
        {!submitted ? (
          <div style={{ background: COLORS.charcoal, padding: "2.5rem", borderRadius: "2px", border: `1px solid #333` }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.4rem", marginBottom: "2rem" }}>
              {formType === "join" ? "Member Sign-Up Form" : "Membership Renewal Form"}
            </h3>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem", marginBottom: "1.2rem" }}>
              <div>
                <label style={labelStyle}>FIRST NAME *</label>
                <input style={inputStyle} name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First name" />
              </div>
              <div>
                <label style={labelStyle}>LAST NAME *</label>
                <input style={inputStyle} name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last name" />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem", marginBottom: "1.2rem" }}>
              <div>
                <label style={labelStyle}>EMAIL ADDRESS *</label>
                <input style={inputStyle} name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" />
              </div>
              <div>
                <label style={labelStyle}>PHONE NUMBER</label>
                <input style={inputStyle} name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="(555) 555-5555" />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem", marginBottom: "1.2rem" }}>
              <div>
                <label style={labelStyle}>EMPLOYER / WORKPLACE *</label>
                <input style={inputStyle} name="employer" value={formData.employer} onChange={handleChange} placeholder="Your employer" />
              </div>
              <div>
                <label style={labelStyle}>SEIU LOCAL</label>
                <select style={{ ...inputStyle }} name="local" value={formData.local} onChange={handleChange}>
                  <option value="">Select your local</option>
                  <option>SEIU Local 6</option>
                  <option>SEIU Local 49</option>
                  <option>SEIU Local 87</option>
                  <option>SEIU Local 105</option>
                  <option>SEIU Local 121RN</option>
                  <option>SEIU Local 503</option>
                  <option>SEIU Local 521</option>
                  <option>SEIU Local 775</option>
                  <option>SEIU Local 1000</option>
                  <option>SEIU Local 2015</option>
                  <option>SEIU-UHW</option>
                  <option>Other</option>
                </select>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem", marginBottom: "1.2rem" }}>
              <div>
                <label style={labelStyle}>STATE</label>
                <select style={{ ...inputStyle }} name="state" value={formData.state} onChange={handleChange}>
                  <option value="">Select state</option>
                  <option>Arizona</option>
                  <option>California</option>
                  <option>Colorado</option>
                  <option>Nevada</option>
                  <option>Oregon</option>
                  <option>Washington</option>
                  <option>Other Western State</option>
                </select>
              </div>
              {formType === "renew" && (
                <div>
                  <label style={labelStyle}>MEMBER ID (if known)</label>
                  <input style={inputStyle} name="memberID" value={formData.memberID} onChange={handleChange} placeholder="AFRAM-XXXX" />
                </div>
              )}
            </div>

            {formType === "renew" && (
              <div style={{ marginBottom: "1.2rem" }}>
                <label style={labelStyle}>ORIGINAL JOIN DATE (approximate)</label>
                <input style={inputStyle} name="joinDate" type="month" value={formData.joinDate} onChange={handleChange} />
              </div>
            )}

            <div style={{
              background: "#0d0d0d", padding: "1rem 1.2rem", borderRadius: "2px",
              marginBottom: "1.5rem", border: `1px solid #2a2a2a`,
            }}>
              <p style={{ fontFamily: "Georgia, serif", color: "#888", fontSize: "0.8rem", lineHeight: 1.6, margin: 0 }}>
                By submitting this form, you consent to receive communications from AFRAM West. Your membership dues support regional organizing, leadership development, and advocacy efforts on behalf of African American SEIU members in the Western Region.
              </p>
            </div>

            <button onClick={handleSubmit} style={{
              width: "100%", background: COLORS.gold, color: COLORS.black,
              border: "none", padding: "1.1rem", cursor: "pointer",
              fontFamily: "Georgia, serif", fontSize: "1rem", fontWeight: 700,
              letterSpacing: "0.15em", borderRadius: "2px",
            }}>
              {formType === "join" ? "SUBMIT APPLICATION & JOIN" : "SUBMIT RENEWAL"}
            </button>
          </div>
        ) : (
          <div style={{
            background: COLORS.charcoal, padding: "3rem 2rem", textAlign: "center",
            borderRadius: "2px", border: `2px solid ${COLORS.gold}`,
          }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✦</div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "2rem", marginBottom: "1rem" }}>
              {formType === "join" ? "Welcome to AFRAM West!" : "Membership Renewed!"}
            </h3>
            <p style={{ fontFamily: "Georgia, serif", color: COLORS.cream, fontSize: "1rem", lineHeight: 1.7, maxWidth: "500px", margin: "0 auto 2rem" }}>
              Thank you, {formData.firstName || "Member"}! Your {formType === "join" ? "application" : "renewal"} has been received. Our Western Region team will follow up with you at {formData.email || "your provided email"} within 2–3 business days.
            </p>
            <button onClick={() => { setSubmitted(false); setFormData({ firstName: "", lastName: "", email: "", phone: "", employer: "", local: "", memberID: "", state: "", joinDate: "", membershipType: "standard" }); }} style={{
              background: "transparent", color: COLORS.gold, border: `1px solid ${COLORS.gold}`,
              padding: "0.75rem 2rem", cursor: "pointer",
              fontFamily: "Georgia, serif", fontSize: "0.85rem", borderRadius: "2px",
            }}>SUBMIT ANOTHER FORM</button>
          </div>
        )}
      </div>

      {/* Benefits section */}
      <div style={{ background: COLORS.cream, padding: "4rem 2rem" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.black, fontSize: "2rem", textAlign: "center", marginBottom: "2.5rem" }}>Why Membership Matters</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.5rem" }}>
            {[
              { Icon: Vote,     title: "Your Voice", desc: "Shape the direction of the Western Region caucus and influence SEIU policy." },
              { Icon: BookOpen, title: "Resources",  desc: "Access leadership development programs, scholarships, and professional tools." },
              { Icon: Users,    title: "Network",    desc: "Connect with thousands of African American workers across five Western states." },
              { Icon: Zap,      title: "Power",      desc: "Collective action is more effective when more members join the movement." },
            ].map(b => (
              <div key={b.title} style={{ background: COLORS.black, padding: "1.8rem", borderRadius: "2px", borderTop: `2px solid ${COLORS.gold}` }}>
                <b.Icon size={28} color={COLORS.gold} style={{ marginBottom: "0.7rem" }} />
                <div style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.1rem", marginBottom: "0.5rem" }}>{b.title}</div>
                <p style={{ fontFamily: "Georgia, serif", color: "#bbb", fontSize: "0.83rem", lineHeight: 1.65 }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── GET INVOLVED ──────────────────────────────────────────────────────────────
function GetInvolvedPage({ setPage }) {
  const ways = [
    { Icon: Calendar,      title: "Attend a Meeting",    desc: "AFRAM West holds monthly regional meetings (virtual and in-person) across California, Arizona, Colorado, Nevada, Oregon, and Washington. All members welcome.", contact: true },
    { Icon: GraduationCap, title: "Leadership Institute", desc: "Apply for our annual Leadership Development Institute — a weekend intensive designed to build the next generation of Black labor leaders in the West.", contact: true },
    { Icon: Megaphone,     title: "Spread the Word",     desc: "Recruit fellow African American SEIU members to join AFRAM West. Every new member strengthens our collective voice.", contact: true },
    { Icon: Monitor,       title: "Digital Organizing",  desc: "Join our social media street team to amplify AFRAM West's message across platforms and reach members who haven't yet connected.", contact: true },
    { Icon: Landmark,      title: "Political Action",    desc: "Volunteer for SEIU political action campaigns and help elect leaders who will fight for African American workers.", contact: true },
    { Icon: Heart,         title: "Donate",              desc: "Support the mission with a financial contribution. Funds go directly toward organizing, scholarships, and regional programs.", contact: false },
  ];

  return (
    <div style={{ paddingTop: "64px", background: COLORS.cream, minHeight: "100vh" }}>
      <div style={{
        background: COLORS.black, padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>TAKE ACTION</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Get Involved</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "500px", margin: "0 auto" }}>
          There are many ways to contribute to the movement. Find your role.
        </p>
      </div>

      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.5rem" }}>
          {ways.map(w => (
            <div key={w.title} style={{ background: COLORS.black, padding: "2rem", borderRadius: "2px", borderLeft: `3px solid ${COLORS.gold}`, display: "flex", flexDirection: "column" }}>
              <w.Icon size={36} color={COLORS.gold} style={{ marginBottom: "1rem" }} />
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.15rem", marginBottom: "0.7rem" }}>{w.title}</h3>
              <p style={{ fontFamily: "Georgia, serif", color: "#bbb", fontSize: "0.85rem", lineHeight: 1.7, flex: 1 }}>{w.desc}</p>
              <div style={{ marginTop: "1.2rem" }}>
                {w.contact
                  ? <a href="mailto:info@aframwest.org" style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.78rem", letterSpacing: "0.1em", textDecoration: "none" }}>GET INVOLVED &rarr;</a>
                  : <button onClick={() => setPage("Donation")} style={{ background: "transparent", border: "none", color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.78rem", letterSpacing: "0.1em", cursor: "pointer", padding: 0 }}>DONATE NOW &rarr;</button>
                }
              </div>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: "3rem", background: COLORS.black, padding: "3rem",
          borderRadius: "2px", textAlign: "center", borderTop: `3px solid ${COLORS.gold}`,
        }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.8rem", marginBottom: "1rem" }}>
            Start by Becoming a Member
          </h2>
          <p style={{ fontFamily: "Georgia, serif", color: "#aaa", marginBottom: "2rem" }}>
            Membership is the foundation of everything we do. Join AFRAM West today.
          </p>
          <button onClick={() => setPage("Membership")} style={{
            background: COLORS.gold, color: COLORS.black, border: "none",
            padding: "0.9rem 2.5rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.9rem", fontWeight: 700,
            letterSpacing: "0.12em", borderRadius: "2px",
          }}>JOIN AFRAM WEST →</button>
        </div>
      </div>
    </div>
  );
}

// ── CONTACT ────────────────────────────────────────────────────────────────────
function ContactPage() {
  const [msg, setMsg] = useState({ name: "", email: "", subject: "", body: "" });
  const [sent, setSent] = useState(false);

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.black} 0%, #1a0f00 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>REACH OUT</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Contact Us</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0" }} />
      </div>

      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "4rem 2rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem" }}>
        {/* Info */}
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.5rem", marginBottom: "1.5rem" }}>AFRAM West</h2>
          {[
            { label: "Region", val: "Western Region" },
            { label: "States", val: "CA · AZ · CO · NV · OR · WA" },
            { label: "National HQ", val: "SEIU AFRAM · P.O. Box 51700, Boston, MA 02205" },

          ].map(item => (
            <div key={item.label} style={{ marginBottom: "1.2rem" }}>
              <div style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.2em", marginBottom: "0.2rem" }}>{item.label.toUpperCase()}</div>
              <div style={{ fontFamily: "Georgia, serif", color: COLORS.cream, fontSize: "0.9rem" }}>{item.val}</div>
            </div>
          ))}
        </div>

        {/* Form */}
        {!sent ? (
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.4rem", marginBottom: "1.5rem" }}>Send a Message</h2>
            {[
              { label: "Your Name", key: "name", type: "text", placeholder: "Full name" },
              { label: "Email", key: "email", type: "email", placeholder: "your@email.com" },
              { label: "Subject", key: "subject", type: "text", placeholder: "Message subject" },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: "1rem" }}>
                <label style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.15em", display: "block", marginBottom: "0.4rem" }}>{f.label.toUpperCase()}</label>
                <input
                  style={{ width: "100%", background: "#111", border: `1px solid #333`, color: COLORS.cream, padding: "0.7rem 1rem", fontFamily: "Georgia, serif", fontSize: "0.9rem", borderRadius: "2px", boxSizing: "border-box" }}
                  type={f.type} placeholder={f.placeholder}
                  value={msg[f.key]} onChange={e => setMsg({ ...msg, [f.key]: e.target.value })}
                />
              </div>
            ))}
            <div style={{ marginBottom: "1.2rem" }}>
              <label style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.15em", display: "block", marginBottom: "0.4rem" }}>MESSAGE</label>
              <textarea
                style={{ width: "100%", background: "#111", border: `1px solid #333`, color: COLORS.cream, padding: "0.7rem 1rem", fontFamily: "Georgia, serif", fontSize: "0.9rem", borderRadius: "2px", boxSizing: "border-box", minHeight: "120px", resize: "vertical" }}
                placeholder="Your message..."
                value={msg.body} onChange={e => setMsg({ ...msg, body: e.target.value })}
              />
            </div>
            <button onClick={async () => {
              try {
                const res = await fetch(FORMSPREE_CONTACT, {
                  method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
                  body: JSON.stringify({ _subject: `AFRAM West Contact — ${msg.subject || "(no subject)"}`, name: msg.name, email: msg.email, subject: msg.subject, message: msg.body }),
                });
                if (res.ok) { setSent(true); } else { alert("Problem sending. Please email info@aframwest.org directly."); }
              } catch { alert("Network error. Please email info@aframwest.org directly."); }
            }} style={{
              width: "100%", background: COLORS.gold, color: COLORS.black,
              border: "none", padding: "0.9rem", cursor: "pointer",
              fontFamily: "Georgia, serif", fontSize: "0.9rem", fontWeight: 700, letterSpacing: "0.12em", borderRadius: "2px",
            }}>SEND MESSAGE</button>
          </div>
        ) : (
          <div style={{ textAlign: "center", paddingTop: "2rem" }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✦</div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.6rem", marginBottom: "0.8rem" }}>Message Sent!</h3>
            <p style={{ fontFamily: "Georgia, serif", color: "#aaa", lineHeight: 1.7 }}>Thank you for reaching out. A member of the AFRAM West leadership team will be in touch soon.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── FOOTER ────────────────────────────────────────────────────────────────────
function Footer({ setPage }) {
  return (
    <footer style={{
      background: "#050505", borderTop: `2px solid ${COLORS.goldDark}`,
      padding: "3rem 2rem 1.5rem",
    }}>
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "2rem", marginBottom: "2rem" }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.5rem", fontWeight: 900, marginBottom: "0.3rem" }}>AFRAM WEST</div>
            <div style={{ fontFamily: "Georgia, serif", color: "#666", fontSize: "0.7rem", letterSpacing: "0.2em", marginBottom: "1rem" }}>SEIU AFRICAN AMERICAN CAUCUS</div>
            <p style={{ fontFamily: "Georgia, serif", color: "#777", fontSize: "0.82rem", lineHeight: 1.6 }}>Western Region — serving African American SEIU members in CA, AZ, CO, NV, OR & WA.</p>
          </div>
          <div>
            <div style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.2em", marginBottom: "1rem" }}>NAVIGATION</div>
            {["Home", "Our Story", "Timeline", "Membership", "Get Involved", "States", "Contact", "Donation", "Board", "Merch", "Organize", "Conference"].map(l => (
              <div key={l} style={{ marginBottom: "0.5rem" }}>
                <button onClick={() => setPage(l)} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", padding: 0 }}>{l}</button>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.2em", marginBottom: "1rem" }}>MEMBERSHIP</div>
            <button onClick={() => setPage("Membership")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", display: "block", marginBottom: "0.5rem", padding: 0 }}>Join AFRAM West</button>
            <button onClick={() => setPage("Membership")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", display: "block", marginBottom: "0.5rem", padding: 0 }}>Renew Membership</button>
            <button onClick={() => setPage("Get Involved")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", display: "block", marginBottom: "0.5rem", padding: 0 }}>Get Involved</button>
            <button onClick={() => setPage("Organize")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", display: "block", padding: 0 }}>Organize a Caucus</button>
          </div>
          <div>
            <div style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.2em", marginBottom: "1rem" }}>NATIONAL CAUCUS</div>
            <a href="https://www.seiuafram.org/" target="_blank" rel="noopener noreferrer" style={{ color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", display: "block", marginBottom: "0.5rem", textDecoration: "none" }}>SEIU AFRAM National ↗</a>
            <a href="https://www.facebook.com/groups/473407339489916" target="_blank" rel="noopener noreferrer" style={{ color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.5rem", textDecoration: "none" }}><ExternalLink size={13} />Facebook Group</a>
            <button onClick={() => setPage("Merch")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", display: "block", marginBottom: "0.5rem", padding: 0 }}>AFRAM Merchandise</button>
            <button onClick={() => setPage("Board")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: "Georgia, serif", fontSize: "0.85rem", cursor: "pointer", display: "block", padding: 0 }}>Executive Board</button>
          </div>
        </div>
        <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.7rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            <span style={{ background: COLORS.seiu, borderRadius: "2px", padding: "0.25rem 0.7rem", fontFamily: "Georgia, serif", color: "#fff", fontSize: "0.65rem", letterSpacing: "0.15em", fontWeight: 700 }}>SEIU</span>
            <span style={{ fontFamily: "Georgia, serif", color: "#555", fontSize: "0.72rem" }}>A caucus of the Service Employees International Union</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.8rem", alignItems: "center" }}>
            <div style={{ fontFamily: "Georgia, serif", color: "#555", fontSize: "0.75rem" }}>&#169; 2026 SEIU AFRAM Western Region</div>
            <div style={{ display: "flex", gap: "1.2rem", alignItems: "center" }}>
              <button onClick={() => setPage("Privacy")} style={{ background: "transparent", border: "none", color: "#555", fontFamily: "Georgia, serif", fontSize: "0.75rem", cursor: "pointer", padding: 0, textDecoration: "underline" }}>Privacy Policy</button>
              <button onClick={() => setPage("Accessibility")} style={{ background: "transparent", border: "none", color: "#555", fontFamily: "Georgia, serif", fontSize: "0.75rem", cursor: "pointer", padding: 0, textDecoration: "underline" }}>Accessibility</button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── WESTERN STATES HUB ────────────────────────────────────────────────────────
function WesternStatesPage({ setPage }) {
  const states = [
    {
      abbr: "CA", name: "California", locals: 8,
      desc: "The largest AFRAM presence in the West, with active caucuses across multiple locals serving healthcare, public sector, and service workers.",
      color: "#C9A84C",
    },
    {
      abbr: "OR", name: "Oregon", locals: 1,
      desc: "SEIU Local 503 anchors AFRAM in Oregon, representing state and local government employees statewide.",
      color: "#7EB8C9",
    },
    {
      abbr: "WA", name: "Washington", locals: 2,
      desc: "SEIU Locals 775 and 1199NW carry the AFRAM mission in Washington, covering caregivers and healthcare workers.",
      color: "#9DC08B",
    },
    {
      abbr: "NV", name: "Nevada", locals: 1,
      desc: "SEIU Local 1107 serves Nevada's public and private sector workers with an active AFRAM caucus.",
      color: "#C4956A",
    },
    {
      abbr: "AZ", name: "Arizona", locals: 1,
      desc: "SEIU Local 99 represents Arizona education workers and maintains an AFRAM chapter in the state.",
      color: "#B07CC6",
    },
    {
      abbr: "CO", name: "Colorado", locals: 1,
      desc: "SEIU Local 105 represents Colorado workers across multiple sectors and connects members to the AFRAM network.",
      color: "#E07B6A",
    },
  ];

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.black} 0%, #1a0f02 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>SEIU AFRAM WESTERN REGION</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Western States</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "560px", margin: "0 auto" }}>
          Select a state to find AFRAM caucuses, local contacts, and resources for African American SEIU members in your area.
        </p>
      </div>

      {/* State cards */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "4rem 2rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1.5rem" }}>
          {states.map(s => (
            <button
              key={s.abbr}
              onClick={() => setPage("State_" + s.abbr)}
              style={{
                background: COLORS.charcoal, border: `1px solid #2a2a2a`,
                borderTop: `4px solid ${s.color}`,
                padding: "2rem", borderRadius: "2px", cursor: "pointer",
                textAlign: "left", transition: "border-color 0.2s, background 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#252525"; e.currentTarget.style.borderColor = s.color; }}
              onMouseLeave={e => { e.currentTarget.style.background = COLORS.charcoal; e.currentTarget.style.borderColor = "#2a2a2a"; }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1rem" }}>
                <div style={{
                  fontFamily: "'Playfair Display', serif", color: s.color,
                  fontSize: "3rem", fontWeight: 900, lineHeight: 1,
                }}>{s.abbr}</div>
                <div style={{
                  background: s.color + "22", border: `1px solid ${s.color}44`,
                  borderRadius: "2px", padding: "0.25rem 0.6rem",
                  fontFamily: "Georgia, serif", color: s.color, fontSize: "0.7rem", letterSpacing: "0.1em",
                }}>{s.locals} LOCAL{s.locals !== 1 ? "S" : ""}</div>
              </div>
              <div style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.2rem", fontWeight: 700, marginBottom: "0.6rem" }}>{s.name}</div>
              <p style={{ fontFamily: "Georgia, serif", color: "#888", fontSize: "0.83rem", lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
              <div style={{ marginTop: "1.2rem", fontFamily: "Georgia, serif", color: s.color, fontSize: "0.75rem", letterSpacing: "0.12em" }}>
                VIEW CAUCUSES →
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── STATE DETAIL PAGE ──────────────────────────────────────────────────────────
function StateDetailPage({ stateAbbr, setPage }) {
  const stateData = {
    CA: {
      name: "California",
      color: "#C9A84C",
      intro: "California is home to the largest concentration of AFRAM caucuses in the Western Region. From healthcare to public services to janitorial workers, African American SEIU members across the state have organized active chapters.",
      locals: [
        {
          name: "SEIU Local 721 – African American Caucus",
          sector: "Public Sector (LA/Southern CA)",
          contact: "vincent.winston@seiu721.org | (213) 716-0227",
          meetings: "2nd Thursday of each month, 6 PM — 1545 Wilshire Blvd., Los Angeles",
          url: "https://www.seiu721.org/category/african-american-caucus",
          urlLabel: "seiu721.org",
        },
        {
          name: "SEIU-UHW AFRAM Caucus",
          sector: "Healthcare Workers (Statewide CA)",
          contact: "uhwafram.com",
          meetings: "Contact local for meeting schedule",
          url: "https://uhwafram.com/",
          urlLabel: "uhwafram.com",
        },
        {
          name: "SEIU Local 521 – AFRAM Caucus",
          sector: "Public Services (Central/South Bay CA)",
          contact: "communications@seiu521.org",
          meetings: "Contact local for meeting schedule",
          url: "https://www.seiu521.org/who-we-are/councils-committees-caucuses",
          urlLabel: "seiu521.org",
        },
        {
          name: "SEIU Local 1000 – AFRAM Caucus",
          sector: "State Employees (Statewide CA)",
          contact: "Contact local for AFRAM chapter info",
          meetings: "Contact local for meeting schedule",
          url: "https://www.seiu1000.org/",
          urlLabel: "seiu1000.org",
        },
        {
          name: "SEIU Local 2015 – AFRAM Caucus",
          sector: "Long-Term Care (Statewide CA)",
          contact: "Contact local for AFRAM chapter info",
          meetings: "Contact local for meeting schedule",
          url: "https://www.seiu2015.org/",
          urlLabel: "seiu2015.org",
        },
        {
          name: "SEIU Local 87 – AFRAM Chapter",
          sector: "Janitors & Building Services (SF Bay Area)",
          contact: "Contact local for AFRAM chapter info",
          meetings: "Contact local for meeting schedule",
          url: "https://www.seiu87.org/",
          urlLabel: "seiu87.org",
        },
        {
          name: "SEIU Local 1021 – AFRAM Caucus",
          sector: "Public Services (Northern CA)",
          contact: "Contact local for AFRAM chapter info",
          meetings: "Contact local for meeting schedule",
          url: "https://www.seiu1021.org/",
          urlLabel: "seiu1021.org",
        },
        {
          name: "SEIU Local 6 – AFRAM Chapter",
          sector: "Building Services (Multiple regions)",
          contact: "Contact local for AFRAM chapter info",
          meetings: "Contact local for meeting schedule",
          url: "https://www.seiu6.org/",
          urlLabel: "seiu6.org",
        },
      ],
    },
    OR: {
      name: "Oregon",
      color: "#7EB8C9",
      intro: "SEIU Local 503 is the primary SEIU local in Oregon, representing over 60,000 public employees including state workers, home care workers, and child care providers. Its AFRAM caucus is active and welcoming to all members.",
      locals: [
        {
          name: "SEIU Local 503 – AFRAM Caucus",
          sector: "Public & Home Care (Statewide OR)",
          contact: "AFRAMCaucus@seiu503.org",
          meetings: "3rd Monday of each month, 6–7:30 PM (virtual)",
          url: "https://seiu503.org/get-involved/join-the-seiu-caucus-community/afram/",
          urlLabel: "seiu503.org/afram",
        },
      ],
    },
    WA: {
      name: "Washington",
      color: "#9DC08B",
      intro: "Washington's AFRAM presence spans two major locals: SEIU 775 representing home care and long-term care workers, and SEIU Healthcare 1199NW representing hospital and healthcare workers across the state.",
      locals: [
        {
          name: "SEIU Healthcare 1199NW – Black Leadership Caucus (AFRAM)",
          sector: "Healthcare (Statewide WA)",
          contact: "Contact the 1199NW Action Center",
          meetings: "Contact local for meeting schedule",
          url: "https://www.seiu1199nw.org/our-community/",
          urlLabel: "seiu1199nw.org",
        },
        {
          name: "SEIU Local 775 – AFRAM Caucus",
          sector: "Long-Term & Home Care (WA, MT, AK)",
          contact: "mrc@seiu775.org | 1-866-371-3200",
          meetings: "Contact local for meeting schedule",
          url: "https://seiu775.org/",
          urlLabel: "seiu775.org",
        },
      ],
    },
    NV: {
      name: "Nevada",
      color: "#C4956A",
      intro: "SEIU Local 1107 serves Nevada's public and private sector workers — including government employees, healthcare workers, and support staff — and maintains an active AFRAM caucus in the state.",
      locals: [
        {
          name: "SEIU Local 1107 – AFRAM Caucus",
          sector: "Public & Private Sector (Statewide NV)",
          contact: "Contact local for AFRAM chapter info",
          meetings: "Contact local for meeting schedule",
          url: "https://seiunv.org/caucuses/afram/",
          urlLabel: "seiunv.org/caucuses/afram",
        },
      ],
    },
    AZ: {
      name: "Arizona",
      color: "#B07CC6",
      intro: "SEIU Local 99 is Arizona's primary SEIU local, representing education support workers across Southern California and Arizona. Members can connect with the AFRAM network through the local's civil rights and equity programs.",
      locals: [
        {
          name: "SEIU Local 99 – AFRAM Chapter",
          sector: "Education Workers (AZ & Southern CA)",
          contact: "Contact local for AFRAM chapter info",
          meetings: "Contact local for meeting schedule",
          url: "https://www.seiu99.org/",
          urlLabel: "seiu99.org",
        },
      ],
    },
    CO: {
      name: "Colorado",
      color: "#E07B6A",
      intro: "SEIU Local 105 is Colorado's leading SEIU local, representing workers in healthcare, building services, and public sector roles throughout the state. Colorado is an expanding chapter in the AFRAM Western Region.",
      locals: [
        {
          name: "SEIU Local 105 – AFRAM Chapter",
          sector: "Healthcare & Service Workers (Statewide CO)",
          contact: "Contact local for AFRAM chapter info",
          meetings: "Contact local for meeting schedule",
          url: "https://www.seiu105.org/",
          urlLabel: "seiu105.org",
        },
      ],
    },
  };

  const state = stateData[stateAbbr];
  if (!state) return null;

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.black} 0%, #1a0f02 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${state.color}`,
      }}>
        {/* Breadcrumb */}
        <button onClick={() => setPage("States")} style={{
          background: "transparent", border: "none", color: "#666",
          fontFamily: "Georgia, serif", fontSize: "0.72rem", letterSpacing: "0.15em",
          cursor: "pointer", marginBottom: "1.5rem", display: "inline-flex", alignItems: "center", gap: "0.4rem",
        }}>← WESTERN STATES</button>

        <div style={{ color: state.color, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>
          AFRAM CAUCUSES · {state.name.toUpperCase()}
        </div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>
          {state.name}
        </h1>
        <div style={{ width: "60px", height: "3px", background: state.color, margin: "1.2rem auto 1.5rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "620px", margin: "0 auto" }}>
          {state.intro}
        </p>
      </div>

      {/* Locals list */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}>
        <div style={{ fontFamily: "Georgia, serif", color: state.color, fontSize: "0.72rem", letterSpacing: "0.3em", marginBottom: "2rem" }}>
          {state.locals.length} LOCAL CAUCUS{state.locals.length !== 1 ? "ES" : ""} FOUND
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem" }}>
          {state.locals.map((local, i) => (
            <div key={i} style={{
              background: COLORS.charcoal, borderLeft: `4px solid ${state.color}`,
              padding: "1.8rem 2rem", borderRadius: "2px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>
                <div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.15rem", fontWeight: 700, marginBottom: "0.3rem" }}>
                    {local.name}
                  </h3>
                  <div style={{ fontFamily: "Georgia, serif", color: state.color, fontSize: "0.72rem", letterSpacing: "0.1em" }}>
                    {local.sector.toUpperCase()}
                  </div>
                </div>
                <a
                  href={local.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: state.color, color: COLORS.black,
                    padding: "0.5rem 1.2rem", borderRadius: "2px",
                    fontFamily: "Georgia, serif", fontSize: "0.75rem",
                    fontWeight: 700, letterSpacing: "0.1em", textDecoration: "none",
                    whiteSpace: "nowrap", flexShrink: 0,
                  }}>
                  VISIT CAUCUS →
                </a>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div>
                  <div style={{ fontFamily: "Georgia, serif", color: "#555", fontSize: "0.65rem", letterSpacing: "0.2em", marginBottom: "0.25rem" }}>CONTACT</div>
                  <div style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "0.82rem" }}>{local.contact}</div>
                </div>
                <div>
                  <div style={{ fontFamily: "Georgia, serif", color: "#555", fontSize: "0.65rem", letterSpacing: "0.2em", marginBottom: "0.25rem" }}>MEETINGS</div>
                  <div style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "0.82rem" }}>{local.meetings}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div style={{
          marginTop: "3rem", background: COLORS.charcoal, padding: "2.5rem",
          borderRadius: "2px", borderTop: `3px solid ${state.color}`, textAlign: "center",
        }}>
          <p style={{ fontFamily: "Georgia, serif", color: "#888", fontSize: "0.9rem", lineHeight: 1.7, marginBottom: "1.5rem" }}>
            Don't see your local listed? Contact AFRAM West to connect with the regional network and find AFRAM members in your workplace.
          </p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => setPage("Contact")} style={{
              background: state.color, color: COLORS.black, border: "none",
              padding: "0.75rem 2rem", cursor: "pointer",
              fontFamily: "Georgia, serif", fontSize: "0.82rem", fontWeight: 700,
              letterSpacing: "0.1em", borderRadius: "2px",
            }}>CONTACT AFRAM WEST</button>
            <button onClick={() => setPage("States")} style={{
              background: "transparent", color: state.color, border: `1px solid ${state.color}`,
              padding: "0.75rem 2rem", cursor: "pointer",
              fontFamily: "Georgia, serif", fontSize: "0.82rem",
              letterSpacing: "0.1em", borderRadius: "2px",
            }}>← ALL STATES</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── WRAAC BOARD ───────────────────────────────────────────────────────────────
function WRAACBoardPage() {
  const board = [
    { role: "President",               name: "Ibrahim Coulibaly",       local: "L503",    category: "Officers" },
    { role: "Executive Vice President", name: "Irene Green",            local: "L1000",   category: "Officers" },
    { role: "Emeritus President",       name: "Carol Maxey-Ware",       local: "L721",    category: "Officers" },
    { role: "Secretary",                name: "Angela D Sanders",       local: "L99",     category: "Officers" },
    { role: "Treasurer",                name: "Elissa Underwood",       local: "LUHW",    category: "Officers" },
    { role: "Recording Secretary",      name: "Marvel Smith",           local: "L503",    category: "Officers" },
    { role: "Historian",                name: "Darnell Parker",         local: "L925",    category: "Officers" },
    { role: "Sergeant at Arms",         name: "Teri Sterling",          local: "L1199NW", category: "Officers" },
    { role: "Parliamentarian",          name: "Diane Carr",             local: "L2015",   category: "Officers" },
    { role: "Chaplain",                 name: "Yolanda Floyd",          local: "L721",    category: "Officers" },
    { role: "Vice President",           name: "Darrell Clark",          local: "L99",     category: "Vice Presidents" },
    { role: "Vice President",           name: "Georgette Bradford",     local: "LUHW",    category: "Vice Presidents" },
    { role: "Vice President",           name: "Gerrell Howard",         local: "L221",    category: "Vice Presidents" },
    { role: "Vice President",           name: "Nanette Carter-Jafri",   local: "L503",    category: "Vice Presidents" },
    { role: "Vice President",           name: "Arvelia Cooper-Whigham", local: "L521",    category: "Vice Presidents" },
    { role: "VP & Fundraiser Chair",    name: "LaShawn Tillman",        local: "L721",    category: "Vice Presidents" },
    { role: "Vice President",           name: "Darryl Johnson",         local: "L775",    category: "Vice Presidents" },
    { role: "Vice President",           name: "Shirley McElroy-Ray",    local: "L925",    category: "Vice Presidents" },
    { role: "Vice President",           name: "Jacquelin Martin",       local: "L1000",   category: "Vice Presidents" },
    { role: "Vice President",           name: "Phelicia Jones",         local: "L1021",   category: "Vice Presidents" },
    { role: "Vice President",           name: 'Carol "Blue" Jackson',  local: "L1876",   category: "Vice Presidents" },
    { role: "Vice President",           name: "Kevin Bolton Jr",        local: "L6",      category: "Vice Presidents" },
    { role: "Vice President",           name: "Stephanie Hughes",       local: "L1107",   category: "Vice Presidents" },
    { role: "Delegate",                 name: "Alysia Bonner",          local: "L521",    category: "Delegates & New Gen" },
    { role: "Delegate",                 name: "Rhonda Johnson",         local: "L925",    category: "Delegates & New Gen" },
    { role: "Delegate",                 name: "Michele Reed",           local: "L2015",   category: "Delegates & New Gen" },
    { role: "New Generation",           name: "Vincent Winston",        local: "L721",    category: "Delegates & New Gen" },
    { role: "Retiree",                  name: "Ronnie Martin",          local: "L99",     category: "Retirees" },
    { role: "Retiree",                  name: "Harold Powell",          local: "L1021",   category: "Retirees" },
    { role: "Retiree",                  name: "Marilyn DeRouen",        local: "L521",    category: "Retirees" },
    { role: "Retiree",                  name: "Carol Harris",           local: "L925",    category: "Retirees" },
    { role: "Staff",                    name: "Nancy Gatewood",         local: "L925",    category: "Staff" },
  ];

  const categories = ["Officers", "Vice Presidents", "Delegates & New Gen", "Retirees", "Staff"];

  const categoryColors = {
    "Officers":             COLORS.gold,
    "Vice Presidents":      "#7EB8C9",
    "Delegates & New Gen":  "#9DC08B",
    "Retirees":             "#C4956A",
    "Staff":                COLORS.gray,
  };

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.black} 0%, #1a0f02 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>SEIU AFRAM WESTERN REGION</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>WRAAC Executive Board</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "540px", margin: "0 auto" }}>
          The dedicated leaders who guide the Western Region African American Caucus in its mission of solidarity, equity, and advocacy.
        </p>
      </div>

      {/* Board by category */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "4rem 2rem" }}>
        {categories.map(cat => {
          const members = board.filter(m => m.category === cat);
          const accent = categoryColors[cat];
          return (
            <div key={cat} style={{ marginBottom: "3.5rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
                <div style={{ height: "2px", flex: 1, background: `linear-gradient(to right, ${accent}, transparent)` }} />
                <h2 style={{ fontFamily: "Georgia, serif", color: accent, fontSize: "0.75rem", letterSpacing: "0.3em", whiteSpace: "nowrap" }}>
                  {cat.toUpperCase()}
                </h2>
                <div style={{ height: "2px", flex: 1, background: `linear-gradient(to left, ${accent}, transparent)` }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "1rem" }}>
                {members.map((m, i) => (
                  <div key={i} style={{
                    background: COLORS.charcoal, borderLeft: `3px solid ${accent}`,
                    padding: "1.2rem 1.4rem", borderRadius: "2px",
                    display: "flex", flexDirection: "column", gap: "0.3rem",
                  }}>
                    <div style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1rem", fontWeight: 700 }}>{m.name}</div>
                    <div style={{ fontFamily: "Georgia, serif", color: accent, fontSize: "0.72rem", letterSpacing: "0.08em" }}>{m.role.toUpperCase()}</div>
                    <div style={{ fontFamily: "Georgia, serif", color: "#666", fontSize: "0.75rem", marginTop: "0.2rem" }}>SEIU {m.local}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        <div style={{ textAlign: "center", marginTop: "1rem", padding: "2rem", borderTop: `1px solid #1f1f1f` }}>
          <div style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "2rem", fontWeight: 900 }}>{board.length}</div>
          <div style={{ fontFamily: "Georgia, serif", color: "#555", fontSize: "0.7rem", letterSpacing: "0.25em" }}>BOARD MEMBERS</div>
        </div>
      </div>
    </div>
  );
}

// ── DONATION ──────────────────────────────────────────────────────────────────
function DonationPage() {
  const [amount, setAmount] = useState("");
  const [custom, setCustom] = useState("");
  const [donated, setDonated] = useState(false);
  const presets = ["$10", "$25", "$50", "$100"];

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.black} 0%, #1a0f00 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>SUPPORT THE MOVEMENT</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Make a Donation</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "500px", margin: "0 auto" }}>
          Your contribution directly funds organizing, scholarships, and advocacy for African American workers across the Western Region.
        </p>
      </div>

      <div style={{ maxWidth: "600px", margin: "0 auto", padding: "4rem 2rem" }}>
        {!donated ? (
          <div style={{ background: COLORS.charcoal, padding: "2.5rem", borderRadius: "2px", border: `1px solid #2a2a2a` }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.4rem", marginBottom: "1.8rem" }}>Choose an Amount</h3>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.8rem", marginBottom: "1.5rem" }}>
              {presets.map(p => (
                <button key={p} onClick={() => { setAmount(p); setCustom(""); }} style={{
                  padding: "0.9rem", background: amount === p ? COLORS.gold : "transparent",
                  color: amount === p ? COLORS.black : COLORS.cream,
                  border: `1px solid ${amount === p ? COLORS.gold : "#444"}`,
                  cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "1rem",
                  fontWeight: 700, borderRadius: "2px", transition: "all 0.2s",
                }}>{p}</button>
              ))}
            </div>

            <div style={{ marginBottom: "1.8rem" }}>
              <label style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.7rem", letterSpacing: "0.15em", display: "block", marginBottom: "0.4rem" }}>CUSTOM AMOUNT</label>
              <input
                style={{ width: "100%", background: "#111", border: `1px solid #333`, color: COLORS.cream, padding: "0.7rem 1rem", fontFamily: "Georgia, serif", fontSize: "0.9rem", borderRadius: "2px", boxSizing: "border-box" }}
                placeholder="Enter amount (e.g. 75)"
                value={custom}
                onChange={e => { setCustom(e.target.value); setAmount(""); }}
              />
            </div>

            <div style={{ background: "#0d0d0d", padding: "1rem 1.2rem", borderRadius: "2px", marginBottom: "1.5rem", border: `1px solid #2a2a2a` }}>
              <p style={{ fontFamily: "Georgia, serif", color: "#888", fontSize: "0.8rem", lineHeight: 1.6, margin: 0 }}>
                Donations support AFRAM West's regional organizing, leadership development programs, and advocacy efforts. AFRAM West is a membership caucus of SEIU.
              </p>
            </div>

            <button
              onClick={() => { if (amount || custom) setDonated(true); }}
              style={{
                width: "100%", background: COLORS.gold, color: COLORS.black,
                border: "none", padding: "1.1rem", cursor: "pointer",
                fontFamily: "Georgia, serif", fontSize: "1rem", fontWeight: 700,
                letterSpacing: "0.15em", borderRadius: "2px",
                opacity: (amount || custom) ? 1 : 0.5,
              }}>
              DONATE {amount || (custom ? `$${custom}` : "")}
            </button>
          </div>
        ) : (
          <div style={{
            background: COLORS.charcoal, padding: "3rem 2rem", textAlign: "center",
            borderRadius: "2px", border: `2px solid ${COLORS.gold}`,
          }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✦</div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "2rem", marginBottom: "1rem" }}>Thank You!</h3>
            <p style={{ fontFamily: "Georgia, serif", color: COLORS.cream, fontSize: "1rem", lineHeight: 1.7, maxWidth: "420px", margin: "0 auto 2rem" }}>
              Your generous donation of {amount || `$${custom}`} helps power the movement for African American workers across the Western Region.
            </p>
            <button onClick={() => { setDonated(false); setAmount(""); setCustom(""); }} style={{
              background: "transparent", color: COLORS.gold, border: `1px solid ${COLORS.gold}`,
              padding: "0.75rem 2rem", cursor: "pointer",
              fontFamily: "Georgia, serif", fontSize: "0.85rem", borderRadius: "2px",
            }}>DONATE AGAIN</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MERCH ─────────────────────────────────────────────────────────────────────
function MerchPage() {
  const [cart, setCart] = useState([]);
  const [added, setAdded] = useState(null);

  const items = [
    { id: 1, name: "AFRAM West T-Shirt", price: 25, desc: "Classic crew-neck tee featuring the AFRAM West logo. 100% cotton, union-printed.", sizes: ["S","M","L","XL","2XL","3XL"], emoji: "👕" },
    { id: 2, name: "AFRAM West Hoodie", price: 45, desc: "Heavyweight pullover hoodie with embroidered AFRAM West crest. Perfect for rallies and events.", sizes: ["S","M","L","XL","2XL","3XL"], emoji: "🧥" },
    { id: 3, name: "AFRAM West Cap", price: 22, desc: "Structured 6-panel cap with embroidered logo. Adjustable snapback.", sizes: ["One Size"], emoji: "🧢" },
    { id: 4, name: "AFRAM West Tote Bag", price: 18, desc: "Heavy-duty canvas tote with the AFRAM West motto. Great for organizing events.", sizes: ["One Size"], emoji: "👜" },
    { id: 5, name: "AFRAM West Button Set", price: 10, desc: "Set of 4 collectible 1.5-inch buttons featuring AFRAM West designs and slogans.", sizes: ["One Size"], emoji: "📌" },
    { id: 6, name: "AFRAM West Mug", price: 16, desc: "11oz ceramic mug with full-color AFRAM West design. Dishwasher safe.", sizes: ["One Size"], emoji: "☕" },
  ];

  const [sizes, setSizes] = useState({});

  const addToCart = (item) => {
    const sz = sizes[item.id] || item.sizes[0];
    setCart(prev => {
      const key = item.id + "-" + sz;
      const exists = prev.find(c => c.key === key);
      if (exists) return prev.map(c => c.key === key ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { key, id: item.id, name: item.name, price: item.price, size: sz, qty: 1, emoji: item.emoji }];
    });
    setAdded(item.id);
    setTimeout(() => setAdded(null), 1500);
  };

  const total = cart.reduce((s, c) => s + c.price * c.qty, 0);

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.black} 0%, #1a0f02 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>REPRESENT THE MOVEMENT</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>AFRAM Merchandise</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "500px", margin: "0 auto" }}>
          Wear your solidarity. Proceeds support AFRAM West organizing, scholarships, and regional programs.
        </p>
      </div>

      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "4rem 2rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr minmax(260px, 300px)", gap: "2.5rem", alignItems: "flex-start" }}>

          {/* Product grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1.2rem" }}>
            {items.map(item => (
              <div key={item.id} style={{
                background: COLORS.charcoal, borderRadius: "2px",
                border: `1px solid #2a2a2a`, borderTop: `3px solid ${COLORS.gold}`,
                padding: "1.6rem", display: "flex", flexDirection: "column",
              }}>
                <div style={{ fontSize: "3rem", marginBottom: "1rem", textAlign: "center" }}>{item.emoji}</div>
                <div style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.05rem", fontWeight: 700, marginBottom: "0.3rem" }}>{item.name}</div>
                <div style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "1.1rem", fontWeight: 700, marginBottom: "0.6rem" }}>${item.price}</div>
                <p style={{ fontFamily: "Georgia, serif", color: "#888", fontSize: "0.8rem", lineHeight: 1.6, marginBottom: "1rem", flex: 1 }}>{item.desc}</p>

                {item.sizes.length > 1 && (
                  <div style={{ marginBottom: "0.8rem" }}>
                    <label style={{ fontFamily: "Georgia, serif", color: "#555", fontSize: "0.65rem", letterSpacing: "0.2em", display: "block", marginBottom: "0.3rem" }}>SIZE</label>
                    <select
                      value={sizes[item.id] || item.sizes[0]}
                      onChange={e => setSizes({ ...sizes, [item.id]: e.target.value })}
                      style={{ background: "#111", border: "1px solid #333", color: COLORS.cream, padding: "0.4rem 0.6rem", fontFamily: "Georgia, serif", fontSize: "0.85rem", borderRadius: "2px", width: "100%" }}
                    >
                      {item.sizes.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                )}

                <button
                  onClick={() => addToCart(item)}
                  style={{
                    background: added === item.id ? COLORS.goldDark : COLORS.gold,
                    color: COLORS.black, border: "none", padding: "0.7rem",
                    cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "0.8rem",
                    fontWeight: 700, letterSpacing: "0.1em", borderRadius: "2px",
                    transition: "background 0.2s",
                  }}>
                  {added === item.id ? "✦ ADDED!" : "ADD TO CART"}
                </button>
              </div>
            ))}
          </div>

          {/* Cart */}
          <div style={{ background: COLORS.charcoal, borderRadius: "2px", border: `1px solid #2a2a2a`, padding: "1.8rem", position: "sticky", top: "80px" }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.2rem", marginBottom: "1.2rem" }}>
              Your Cart {cart.length > 0 && <span style={{ fontSize: "0.9rem", color: "#aaa" }}>({cart.reduce((s,c)=>s+c.qty,0)} items)</span>}
            </h3>

            {cart.length === 0 ? (
              <p style={{ fontFamily: "Georgia, serif", color: "#555", fontSize: "0.85rem", lineHeight: 1.6 }}>Your cart is empty. Add some items to get started.</p>
            ) : (
              <>
                {cart.map(c => (
                  <div key={c.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.8rem", paddingBottom: "0.8rem", borderBottom: "1px solid #2a2a2a" }}>
                    <div>
                      <div style={{ fontFamily: "Georgia, serif", color: COLORS.cream, fontSize: "0.82rem" }}>{c.emoji} {c.name}</div>
                      <div style={{ fontFamily: "Georgia, serif", color: "#666", fontSize: "0.72rem" }}>{c.size} × {c.qty}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.85rem" }}>${c.price * c.qty}</span>
                      <button onClick={() => setCart(prev => prev.filter(x => x.key !== c.key))} style={{ background: "transparent", border: "none", color: "#555", cursor: "pointer", fontSize: "0.85rem" }}>✕</button>
                    </div>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1rem", paddingTop: "1rem", borderTop: `1px solid ${COLORS.goldDark}` }}>
                  <span style={{ fontFamily: "Georgia, serif", color: COLORS.cream, fontWeight: 700 }}>Total</span>
                  <span style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.2rem", fontWeight: 900 }}>${total}</span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await fetch(FORMSPREE_CONTACT, {
                        method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
                        body: JSON.stringify({ _subject: "AFRAM West Merch Order", orderItems: cart.map(ci => ci.emoji + " " + ci.name + " (" + ci.size + ") x" + ci.qty + " — $" + (ci.price * ci.qty)).join(", "), orderTotal: "$" + cart.reduce((s,ci) => s + ci.price * ci.qty, 0) }),
                      });
                    } catch {}
                    setCart([]); alert("Order submitted! Our team will follow up with payment and shipping details.");
                  }}
                  style={{
                    width: "100%", background: COLORS.gold, color: COLORS.black,
                    border: "none", padding: "0.9rem", cursor: "pointer",
                    fontFamily: "Georgia, serif", fontSize: "0.85rem", fontWeight: 700,
                    letterSpacing: "0.12em", borderRadius: "2px", marginTop: "1rem",
                  }}>PLACE ORDER</button>
                <p style={{ fontFamily: "Georgia, serif", color: "#444", fontSize: "0.68rem", marginTop: "0.8rem", lineHeight: 1.5 }}>
                  Our team will contact you to arrange payment and shipping after your order is submitted.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ORGANIZE ──────────────────────────────────────────────────────────────────
function OrganizePage({ setPage }) {
  const steps = [
    {
      num: "01",
      title: "Assess Interest in Your Local",
      desc: "Start by identifying fellow African American SEIU members in your local who share an interest in forming a caucus. Informal conversations, union meetings, and social media groups are great starting points. You need a core group of at least 5–10 committed members before moving forward.",
      tips: ["Talk to coworkers at union meetings", "Connect with your local's Civil & Human Rights Committee", "Reach out through social media or email lists"],
    },
    {
      num: "02",
      title: "Contact AFRAM West Leadership",
      desc: "Reach out to AFRAM West to express your interest and get guidance. Our regional leadership will connect you with resources, share the national AFRAM constitution and guidelines, and help you understand the requirements for forming an official chapter.",
      tips: ["Email west@seiuafram.org to start the conversation", "Request a copy of the SEIU AFRAM Caucus Guidelines", "Ask about upcoming regional conferences you can attend"],
    },
    {
      num: "03",
      title: "Engage Your Local Union Leadership",
      desc: "Caucus formation works best with your local's support. Present the idea to your local's executive board or president. Explain AFRAM's mission and how a chapter strengthens the local by elevating Black member leadership and participation.",
      tips: ["Prepare a short written proposal", "Cite SEIU's official Caucus Guidelines (approved by IEB)", "Connect with your local's Civil & Human Rights staff"],
    },
    {
      num: "04",
      title: "Hold a Founding Meeting",
      desc: "Convene your first official meeting. This is where you establish your chapter's initial leadership, adopt the AFRAM mission statement, and set a meeting schedule. AFRAM West can send a representative to help facilitate.",
      tips: ["Set a regular monthly meeting time", "Elect temporary officers (chair, secretary, treasurer)", "Collect membership applications and dues"],
    },
    {
      num: "05",
      title: "Draft Your Chapter Bylaws",
      desc: "Every chapter needs its own bylaws that comply with the SEIU AFRAM national constitution. Bylaws establish your chapter's structure, officer roles, election process, and meeting procedures. AFRAM West can provide a template.",
      tips: ["Use the AFRAM national constitution as a guide", "Define officer terms and election cycles", "Submit bylaws to AFRAM West and your local for approval"],
    },
    {
      num: "06",
      title: "Register with SEIU AFRAM Nationally",
      desc: "Submit your chapter's formation documents — including your bylaws, officer list, and membership roster — to the SEIU AFRAM national office. Official recognition connects your chapter to national resources, funding, and conference participation.",
      tips: ["Visit seiuafram.org for national contact info", "Attend the national AFRAM conference when possible", "Connect with other regional chapters for mentorship"],
    },
  ];

  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.black} 0%, #1a0f02 100%)`,
        padding: "5rem 2rem 4rem", textAlign: "center",
        borderBottom: `2px solid ${COLORS.gold}`,
      }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>BUILD THE MOVEMENT</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>Organize an AFRAM Caucus</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "580px", margin: "0 auto" }}>
          Every AFRAM chapter begins with a small group of committed workers. Here's how to build one in your SEIU local.
        </p>
      </div>

      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}>

        {/* Intro callout */}
        <div style={{
          background: COLORS.charcoal, borderLeft: `4px solid ${COLORS.gold}`,
          padding: "1.8rem 2rem", borderRadius: "2px", marginBottom: "3.5rem",
        }}>
          <p style={{ fontFamily: "Georgia, serif", color: COLORS.cream, fontSize: "0.95rem", lineHeight: 1.8, margin: 0 }}>
            Under SEIU's official Caucus Guidelines, any group of members can form a local AFRAM chapter. Caucuses give African American workers a dedicated space to organize, develop leadership, and advocate within their local — and connect to the broader AFRAM Western Region and national network.
          </p>
        </div>

        {/* Steps */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", marginBottom: "4rem" }}>
          {steps.map((step, i) => (
            <div key={i} style={{
              background: COLORS.charcoal, borderRadius: "2px",
              border: `1px solid #2a2a2a`, padding: "2rem",
              display: "flex", gap: "1.8rem", alignItems: "flex-start",
            }}>
              <div style={{
                fontFamily: "'Playfair Display', serif", color: COLORS.gold,
                fontSize: "2.5rem", fontWeight: 900, lineHeight: 1,
                minWidth: "2.8rem", opacity: 0.6,
              }}>{step.num}</div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.15rem", fontWeight: 700, marginBottom: "0.7rem" }}>
                  {step.title}
                </h3>
                <p style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "0.875rem", lineHeight: 1.75, marginBottom: "1rem" }}>
                  {step.desc}
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                  {step.tips.map((tip, j) => (
                    <div key={j} style={{
                      background: "rgba(201,168,76,0.08)", border: `1px solid rgba(201,168,76,0.2)`,
                      borderRadius: "2px", padding: "0.3rem 0.7rem",
                      fontFamily: "Georgia, serif", color: "#999", fontSize: "0.75rem",
                    }}>✦ {tip}</div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Resources */}
        <div style={{ background: COLORS.charcoal, padding: "2.5rem", borderRadius: "2px", borderTop: `3px solid ${COLORS.gold}`, marginBottom: "2rem" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "1.5rem", marginBottom: "1.5rem" }}>Key Resources</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1rem" }}>
            {[
              { label: "SEIU AFRAM National", desc: "National caucus homepage, resources, and contact info.", url: "https://www.seiuafram.org/", urlLabel: "seiuafram.org" },
              { label: "SEIU Caucus Guidelines", desc: "Official IEB-approved guidelines for forming and running a caucus.", url: "https://convention-docs.seiu.org/2024/SEIUCaucusGuidelines-approvedbyIEB-on-4-18-24-English.pdf", urlLabel: "Download PDF" },
              { label: "AFRAM West Contact", desc: "Reach out to AFRAM West leadership for personalized organizing support.", url: "mailto:west@seiuafram.org", urlLabel: "west@seiuafram.org" },
            ].map((r, i) => (
              <div key={i} style={{ background: "#111", padding: "1.2rem", borderRadius: "2px", borderLeft: `3px solid ${COLORS.goldDark}` }}>
                <div style={{ fontFamily: "Georgia, serif", color: COLORS.gold, fontSize: "0.75rem", letterSpacing: "0.1em", marginBottom: "0.4rem" }}>{r.label.toUpperCase()}</div>
                <p style={{ fontFamily: "Georgia, serif", color: "#888", fontSize: "0.8rem", lineHeight: 1.6, marginBottom: "0.7rem" }}>{r.desc}</p>
                <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: "Georgia, serif", color: COLORS.goldLight, fontSize: "0.78rem", textDecoration: "none" }}>
                  {r.urlLabel} →
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{ textAlign: "center" }}>
          <button onClick={() => setPage("Contact")} style={{
            background: COLORS.gold, color: COLORS.black, border: "none",
            padding: "0.9rem 2.5rem", cursor: "pointer",
            fontFamily: "Georgia, serif", fontSize: "0.9rem", fontWeight: 700,
            letterSpacing: "0.12em", borderRadius: "2px",
          }}>CONTACT AFRAM WEST TO GET STARTED</button>
        </div>
      </div>
    </div>
  );
}


// ── CONFERENCE 2026 ───────────────────────────────────────────────────────────
function ConferencePage({ setPage }) {
  return (
    <div style={{ paddingTop: "64px", background: COLORS.black, minHeight: "100vh" }}>
      <div style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #120800 100%)", padding: "5rem 2rem 4rem", textAlign: "center", borderBottom: "2px solid #C9A84C" }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>SEIU AFRAM WESTERN REGION</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900 }}>WRAAC 2026 Conference</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", maxWidth: "560px", margin: "0 auto" }}>
          Join AFRAM West leadership and members for the 2026 Western Region African American Caucus Conference.
        </p>
      </div>
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "4rem 2rem" }}>
        <div style={{ textAlign: "center", marginBottom: "3rem" }}>
          <a href="https://canva.link/hab7qenco3ady52" target="_blank" rel="noopener noreferrer"
            style={{ display: "inline-block", background: COLORS.charcoal, border: "2px solid #C9A84C", borderRadius: "2px", padding: "2rem 3.5rem", textDecoration: "none" }}>
            <FileText size={48} color={COLORS.gold} style={{ margin: "0 auto 1rem" }} />
            <div style={{ fontFamily: "'Playfair Display', serif", color: COLORS.gold, fontSize: "1.3rem", fontWeight: 900, marginBottom: "0.4rem" }}>View Conference Flyer</div>
            <div style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "0.82rem" }}>Full details, dates and location</div>
          </a>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.5rem", marginBottom: "3rem" }}>
          {[
            { Icon: ClipboardList, title: "Register Now",   desc: "Secure your spot at the 2026 WRAAC Conference.", color: COLORS.gold,  note: "Registration link coming soon" },
            { Icon: Building2,     title: "Reserve Hotel",  desc: "Book your room at the official conference hotel at the group rate.", color: "#7EB8C9", note: "Hotel booking link coming soon" },
            { Icon: CreditCard,    title: "Submit Payment", desc: "Complete your conference registration payment securely online.", color: "#9DC08B", note: "Payment link coming soon" },
          ].map(a => (
            <div key={a.title} style={{ background: COLORS.charcoal, borderRadius: "2px", borderTop: "3px solid " + a.color, padding: "2rem", display: "flex", flexDirection: "column" }}>
              <a.Icon size={36} color={a.color} style={{ marginBottom: "1rem" }} />
              <div style={{ fontFamily: "'Playfair Display', serif", color: a.color, fontSize: "1.15rem", fontWeight: 900, marginBottom: "0.5rem" }}>{a.title}</div>
              <p style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "0.83rem", lineHeight: 1.65, marginBottom: "1.2rem", flex: 1 }}>{a.desc}</p>
              <div style={{ fontFamily: "Georgia, serif", color: "#555", fontSize: "0.72rem", fontStyle: "italic" }}>{a.note}</div>
            </div>
          ))}
        </div>
        <div style={{ background: COLORS.charcoal, padding: "2rem 2.5rem", borderRadius: "2px", borderLeft: "4px solid #C9A84C", marginBottom: "2rem" }}>
          <p style={{ fontFamily: "Georgia, serif", color: COLORS.cream, fontSize: "0.95rem", lineHeight: 1.7, margin: 0 }}>
            For conference questions or to get on the notification list:{" "}
            <a href="mailto:info@aframwest.org" style={{ color: COLORS.gold, textDecoration: "none" }}>info@aframwest.org</a>
          </p>
        </div>
        <div style={{ textAlign: "center" }}>
          <button onClick={() => setPage("Contact")} style={{ background: COLORS.gold, color: COLORS.black, border: "none", padding: "0.9rem 2.5rem", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "0.9rem", fontWeight: 700, letterSpacing: "0.12em", borderRadius: "2px" }}>
            CONTACT US ABOUT THE CONFERENCE
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PRIVACY POLICY ────────────────────────────────────────────────────────────
function PrivacyPolicyPage() {
  const intro1 = "SEIU AFRAM is committed to protecting the privacy of our members, supporters, and website visitors. This Privacy Policy outlines how we collect, use, and safeguard your personal information when you visit our website and interact with our digital services.";
  const intro2 = "By using our website, you agree to the terms of this Privacy Policy. If you do not agree, please do not use our website.";
  const sections = [
    { heading: "1. Introduction", paras: [intro1, intro2] },
    { heading: "2. Information We Collect", paras: [
      "Personal Information: Name, email address, phone number, mailing address, and other details you provide when signing up for membership, donating, subscribing to newsletters, or filling out contact forms.",
      "Payment Information: If you make a donation or pay membership dues, we collect payment details through a secure third-party payment processor. SEIU AFRAM does not store your payment information.",
      "Website Usage Data: We may collect non-personal information such as IP addresses, browser type, pages visited, and referring websites to analyze site performance and improve user experience.",
    ]},
    { heading: "3. How We Use Your Information", paras: [
      "We use collected information to process membership applications, event registrations, and donations; to send updates and newsletters; to improve our website and services; and to comply with legal obligations.",
    ]},
    { heading: "4. Information Sharing & Security", paras: [
      "We do not sell, trade, or rent your personal information to third parties. We may share data with trusted partners for website hosting, payment processing, or communications, but they are required to keep your information confidential.",
      "We implement industry-standard security measures to protect your data, but no system is completely secure.",
    ]},
    { heading: "5. Your Rights & Choices", paras: [
      "You may unsubscribe from our communications at any time. You can request access, updates, or deletion of your personal data by contacting SEIU AFRAM. You can adjust browser settings to limit tracking via cookies.",
    ]},
    { heading: "6. External Links", paras: [
      "Our website may contain links to third-party websites. We are not responsible for the privacy practices of those sites. We encourage users to review their policies before providing personal information.",
    ]},
    { heading: "7. Updates to This Policy", paras: [
      "We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Continued use of the website after changes constitutes acceptance.",
    ]},
    { heading: "8. Contact Us", paras: [
      "For questions about this Privacy Policy, contact us at info@aframwest.org or write to: SEIU AFRAM, P.O. Box 51700, Boston, MA 02205.",
    ]},
  ];
  return (
    <div style={{ paddingTop: "64px", background: COLORS.cream, minHeight: "100vh" }}>
      <div style={{ background: COLORS.black, padding: "5rem 2rem 4rem", textAlign: "center", borderBottom: "2px solid #C9A84C" }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>LEGAL</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem,5vw,3rem)", fontWeight: 900 }}>Privacy Policy</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", fontSize: "0.85rem" }}>Effective Date: March 1, 2025</p>
      </div>
      <div style={{ maxWidth: "780px", margin: "0 auto", padding: "4rem 2rem" }}>
        {sections.map((s, i) => (
          <div key={i} style={{ marginBottom: "2.5rem" }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.black, fontSize: "1.3rem", fontWeight: 900, marginBottom: "0.8rem", paddingLeft: "1rem", borderLeft: "4px solid #C9A84C" }}>{s.heading}</h2>
            {s.paras.map((p, j) => <p key={j} style={{ fontFamily: "Georgia, serif", color: "#333", fontSize: "0.95rem", lineHeight: 1.8, marginBottom: "0.6rem" }}>{p}</p>)}
          </div>
        ))}
        <div style={{ borderTop: "1px solid #ccc", paddingTop: "1.5rem", textAlign: "center" }}>
          <a href="mailto:info@aframwest.org" style={{ fontFamily: "Georgia, serif", color: COLORS.goldDark, fontSize: "0.9rem" }}>info@aframwest.org</a>
        </div>
      </div>
    </div>
  );
}

// ── ACCESSIBILITY STATEMENT ───────────────────────────────────────────────────
function AccessibilityPage() {
  const sections = [
    { heading: "1. Commitment to Accessibility",  body: "SEIU AFRAM Western Region is committed to ensuring that our website is accessible to all users, including individuals with disabilities. We strive to provide an inclusive digital experience that aligns with the Web Content Accessibility Guidelines (WCAG) 2.1." },
    { heading: "2. Accessibility Features",       body: "Our website includes keyboard navigation, screen reader compatibility, descriptive alt text for images, high-contrast text and scalable fonts, and captions or transcripts for multimedia content where applicable." },
    { heading: "3. Ongoing Improvements",         body: "We continuously work to enhance accessibility by conducting regular audits, gathering user feedback to address barriers, and implementing best practices in web design and development." },
    { heading: "4. Third-Party Content",          body: "While we strive for accessibility across all areas of our site, some third-party content or external links may not meet accessibility standards. We encourage users to contact those providers directly if they encounter barriers." },
    { heading: "5. Reporting Accessibility Issues", body: "If you experience difficulties accessing any part of our website, please contact us at info@aframwest.org. We will make reasonable efforts to address accessibility concerns." },
    { heading: "6. Policy Updates",              body: "We may update this Accessibility Statement periodically to reflect improvements or compliance with evolving standards. Changes will be posted on this page with the latest effective date." },
  ];
  return (
    <div style={{ paddingTop: "64px", background: COLORS.cream, minHeight: "100vh" }}>
      <div style={{ background: COLORS.black, padding: "5rem 2rem 4rem", textAlign: "center", borderBottom: "2px solid #C9A84C" }}>
        <div style={{ color: COLORS.gold, fontFamily: "Georgia, serif", fontSize: "0.7rem", letterSpacing: "0.3em", marginBottom: "0.8rem" }}>LEGAL</div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.cream, fontSize: "clamp(2rem,5vw,3rem)", fontWeight: 900 }}>Accessibility Statement</h1>
        <div style={{ width: "60px", height: "3px", background: COLORS.gold, margin: "1.2rem auto 0.8rem" }} />
        <p style={{ fontFamily: "Georgia, serif", color: "#999", fontSize: "0.85rem" }}>Effective Date: March 1, 2025</p>
      </div>
      <div style={{ maxWidth: "780px", margin: "0 auto", padding: "4rem 2rem" }}>
        {sections.map((s, i) => (
          <div key={i} style={{ marginBottom: "2.5rem" }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.black, fontSize: "1.3rem", fontWeight: 900, marginBottom: "0.8rem", paddingLeft: "1rem", borderLeft: "4px solid #C9A84C" }}>{s.heading}</h2>
            <p style={{ fontFamily: "Georgia, serif", color: "#333", fontSize: "0.95rem", lineHeight: 1.8 }}>{s.body}</p>
          </div>
        ))}
        <div style={{ borderTop: "1px solid #ccc", paddingTop: "1.5rem", textAlign: "center" }}>
          <a href="mailto:info@aframwest.org" style={{ fontFamily: "Georgia, serif", color: COLORS.goldDark, fontSize: "0.9rem" }}>info@aframwest.org</a>
        </div>
      </div>
    </div>
  );
}

// ── APP ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("Home");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const renderPage = () => {
    switch (page) {
      case "Home": return <HomePage setPage={setPage} />;
      case "Our Story": return <OurStoryPage setPage={setPage} />;
      case "Timeline": return <TimelinePage />;
      case "Membership": return <MembershipPage />;
      case "Get Involved": return <GetInvolvedPage setPage={setPage} />;
      case "States": return <WesternStatesPage setPage={setPage} />;
      case "Contact": return <ContactPage />;
      case "Donation": return <DonationPage />;
      case "Board": return <WRAACBoardPage />;
      case "Merch": return <MerchPage />;
      case "Organize": return <OrganizePage setPage={setPage} />;
      case "Conference": return <ConferencePage setPage={setPage} />;
      case "Privacy": return <PrivacyPolicyPage />;
      case "Accessibility": return <AccessibilityPage />;
      default:
        if (page.startsWith("State_")) return <StateDetailPage stateAbbr={page.replace("State_", "")} setPage={setPage} />;
        return <HomePage setPage={setPage} />;
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #0a0a0a; }
        input, select, textarea { outline: none; }
        input:focus, select:focus, textarea:focus { border-color: #C9A84C !important; }
        @media (max-width: 640px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: block !important; }
        }
        @media (max-width: 640px) {
          [style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <Nav page={page} setPage={setPage} />
      <main>{renderPage()}</main>
      <Footer setPage={setPage} />
    </>
  );
}
